import { Injectable } from '@angular/core';
import { AppUser, IAppUser } from '../models/app-user.model';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { Guard, IGuard } from '../models/guard.model';

@Injectable({
  providedIn: 'root'
})
export class AppGuardService {

  public loading: boolean = false;
  public guards: Guard[] = [];

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create(
    neighborhoodId: string,
    email: string | null = null
  ): Promise<AppUser | undefined> {
      
    let appUser = undefined;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/guards`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Creando vigilante';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{user: IAppUser}>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{user: IAppUser}>>(
            urlApi,
            JSON.stringify({
              email
            }),
            {
              headers: {
                'Authorization': token.toString(),
                'content-type': 'application/json'
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<{user: IAppUser}>
              ) => resolve(
                new ServerResponse<{user: IAppUser}>(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 201) {
        appUser = new AppUser(response.data!.user);

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          neighborhoodId
        );

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {
      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return appUser;

  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  async delete(
    neighborhoodId: string,
    uidGuard: string
  ): Promise<boolean> {

    let flag = false;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Borrando vigilante';

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/guards/${uidGuard}`;

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(
            urlApi,
            {
              headers: {
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            }
          });
        }
      );

      if (response.statusCode === 200) {
        flag = true;
        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          neighborhoodId
        );
      } else {
        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );
      }

    } catch (error) {
      console.log(error);
      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    return flag;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR getAll                               */
  /* -------------------------------------------------------------------------- */
  // region getAll
  async getAll(
    neighborhoodId: string
  ): Promise<AppUser[]> {

    let guards: AppUser[] = [];

    this.loading = true;
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/guards`;

    try {

      const token = await this.authService.getToken();
      
      const response = await new Promise<ServerResponse<{ guards: IGuard[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ guards: IGuard[] }>>(
            urlApi,
            {
              headers: {
                "content-type": "application/json",
                "Authorization": token.toString()
              }
            }
          ).subscribe({
            next: (response) => resolve(new ServerResponse<{ guards: IGuard[] }>(response)),
            error: (error) => reject(error),
          });
        }
      );

      if (response.statusCode === 200) {
        this.guards = response.data!.guards.map(
          user => new Guard(user)
        );
      }

    } catch(error) {
      this.toastService.show({
        header: 'Error de servidor',
        body: `${error}`,
        classname: 'bg-danger text-white'
      });
    }

    this.loading = false;

    return guards;

  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR update                               */
  /* -------------------------------------------------------------------------- */
  // region update
  async update(
    guard: any,
    userGuard: AppUser
  ): Promise<boolean> {
      
    let flag = false;

    const urlApi = `${environment.urlApi}/neighborhoods/${guard.neighborhoodId}/guards/${userGuard.uid}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando vigilante';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(
            urlApi,
            JSON.stringify(guard),
            {
              headers: {
                'Authorization': token.toString(),
                'content-type': 'application/json'
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse
              ) => resolve(
                new ServerResponse(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {

        flag = true;

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          guard.neighborhoodId
        );

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {
      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;

  }

  /* -------------------------------------------------------------------------- */
  /*                           ANCHOR restorePassword                           */
  /* -------------------------------------------------------------------------- */
  // region restorePassword
  async restorePassword(
    neighborhoodId: string,
    guard: Guard
  ): Promise<AppUser | undefined> {
    
    let appUser = undefined;

    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/guards/${guard.uidGuard}/restore-password`;

    this.spinnerService.loading = true;
    this.spinnerService.message = guard.guard!.username != ""
      ? 'Restaurando contraseña del vigilante'
      : 'Enviando liga para restablecer contraseña del vigilante';

    try {

      const token = await this.authService.getToken();

      const response = await new Promise<ServerResponse<{user: IAppUser}>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{user: IAppUser}>>(
            urlApi,
            {},
            {
              headers: {
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (
                response: IServerResponse<{user: IAppUser}>
              ) => resolve(
                new ServerResponse<{user: IAppUser}>(response)
              ),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
                this.toastService.show(
                  {
                    header: 'Error de servidor',
                    body: `${error}`,
                    classname: 'bg-danger text-white'
                  }
                );
              }
            },
          });
        }
      );

      console.log(response);

      if(response.statusCode === 200) {

        console.log('response.data', response.data);
        

        appUser = new AppUser(response.data!.user);

        this.toastService.show(
          {
            body: `${response.message}`,
            classname: 'bg-success text-white'
          }
        );

        this.getAll(
          neighborhoodId
        );

      } else {

        this.toastService.show(
          {
            header: 'Error de servidor',
            body: `${response.message}`,
            classname: 'bg-danger text-white'
          }
        );

      }

    } catch(error) {
      console.log(error);

      this.toastService.show(
        {
          header: 'Error',
          body: `${error}`,
          classname: 'bg-danger text-white'
        }
      );
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return appUser;

  }

}
